<template>
  <div class="categories">
    <div class="header">
      <h1 class="title">分类管理</h1>
    </div>

    <div class="content">
      <div class="category-list" v-if="categories.length > 0">
        <div 
          v-for="category in categories" 
          :key="category.id"
          class="category-item"
          @click="showCategoryItems(category)"
        >
          <div class="category-info">
            <div class="category-icon">
              <van-icon name="label-o" size="24" :color="getRandomColor(category.id)" />
            </div>
            <div class="category-details">
              <h3 class="category-name">{{ category.get('name') }}</h3>
              <p class="category-desc" v-if="category.get('description')">
                {{ category.get('description') }}
              </p>
              <div class="category-stats">
                <span>{{ getCategoryItemCount(category) }} 个事项</span>
              </div>
            </div>
            <van-icon name="arrow" class="arrow-icon" />
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <van-empty
          image="search"
          description="还没有任何分类"
        >
          <template #bottom>
            <p class="empty-hint">点击下方按钮创建分类</p>
          </template>
        </van-empty>
      </div>
    </div>

    <van-button
      type="primary"
      class="add-button"
      round
      icon="plus"
      @click="showCreateDialog"
    >
      新建分类
    </van-button>

    <van-dialog
      v-model:show="showDialog"
      title="新建分类"
      show-cancel-button
      @confirm="createCategory"
      class="create-dialog"
    >
      <van-form @submit="createCategory">
        <van-field
          v-model="newCategory.name"
          name="name"
          label="名称"
          placeholder="请输入分类名称"
          :rules="[{ required: true, message: '请填写分类名称' }]"
        />
        <van-field
          v-model="newCategory.description"
          name="description"
          label="描述"
          type="textarea"
          placeholder="请输入分类描述"
          rows="3"
          autosize
        />
      </van-form>
    </van-dialog>

    <van-popup
      v-model:show="showItems"
      position="right"
      :style="{ width: '100%', height: '100%' }"
      class="items-popup"
    >
      <div class="popup-header">
        <van-nav-bar
          :title="currentCategory?.get('name')"
          left-arrow
          @click-left="showItems = false"
        >
          <template #right>
            <van-icon 
              name="edit" 
              size="18" 
              @click="showEditCategory"
            />
          </template>
        </van-nav-bar>
      </div>

      <div class="popup-content">
        <div v-if="categoryItems.length > 0" class="items-list">
          <div 
            v-for="item in categoryItems" 
            :key="item.id"
            class="item-card"
            @click="goToDetail(item)"
          >
            <div class="item-status">
              <van-tag 
                :type="isCompleted(item) ? 'success' : 'primary'"
                size="medium"
              >
                {{ isCompleted(item) ? '已完成' : '进行中' }}
              </van-tag>
            </div>
            <h3 class="item-title">{{ item.get('title') }}</h3>
            <p class="item-desc" v-if="item.get('description')">
              {{ item.get('description') }}
            </p>
            <div class="item-progress">
              <div class="progress-text">
                <span>完成进度</span>
                <span>{{ getStepsInfo(item).completed }}/{{ getStepsInfo(item).total }}</span>
              </div>
              <div class="progress-bar">
                <div 
                  class="progress-fill"
                  :style="{ width: getStepsInfo(item).percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <van-empty 
          v-else 
          description="暂无事项"
          class="empty-items"
        />
      </div>
    </van-popup>

    <van-dialog
      v-model:show="showEdit"
      title="编辑分类"
      show-cancel-button
      @confirm="saveEdit"
      class="edit-dialog"
    >
      <van-form>
        <van-field
          v-model="editForm.name"
          label="名称"
          placeholder="请输入分类名称"
          :rules="[{ required: true, message: '请填写分类名称' }]"
        />
        <van-field
          v-model="editForm.description"
          label="描述"
          type="textarea"
          placeholder="请输入分类描述"
          rows="3"
          autosize
        />
      </van-form>
    </van-dialog>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AV from 'leancloud-storage'
import { showToast } from 'vant'

export default {
  name: 'Categories',
  setup() {
    const store = useStore()
    const router = useRouter()
    const showDialog = ref(false)
    const showItems = ref(false)
    const showEdit = ref(false)
    const currentCategory = ref(null)
    const categoryItems = ref([])
    
    const newCategory = ref({
      name: '',
      description: ''
    })

    const editForm = ref({
      name: '',
      description: ''
    })

    const categories = computed(() => store.state.categories)

    // 生成随机颜色
    const colors = ['#007AFF', '#34C759', '#FF9500', '#FF2D55', '#5856D6', '#FF3B30']
    const getRandomColor = (id) => {
      const index = id.charCodeAt(0) % colors.length
      return colors[index]
    }

    const getCategoryItemCount = (category) => {
      // 这里可以添加获取分类下事项数量的逻辑
      return categoryItems.value.length
    }

    const showCategoryItems = async (category) => {
      currentCategory.value = category
      const query = new AV.Query('ChecklistItem')
      query.equalTo('category', category)
      query.equalTo('user', AV.User.current())
      
      try {
        categoryItems.value = await query.find()
        showItems.value = true
      } catch (error) {
        showToast('获取数据失败')
      }
    }

    const createCategory = async () => {
      if (!newCategory.value.name) return
      
      const Category = AV.Object.extend('Category')
      const category = new Category()
      
      category.set({
        name: newCategory.value.name,
        description: newCategory.value.description || '',
        user: AV.User.current()
      })

      try {
        await category.save()
        await store.dispatch('fetchCategories')
        showDialog.value = false
        newCategory.value = { name: '', description: '' }
        showToast('创建成功')
      } catch (error) {
        showToast('创建失败')
      }
    }

    const showCreateDialog = () => {
      showDialog.value = true
    }

    const showEditCategory = () => {
      if (!currentCategory.value) return
      
      editForm.value = {
        name: currentCategory.value.get('name'),
        description: currentCategory.value.get('description') || ''
      }
      showEdit.value = true
    }

    const saveEdit = async () => {
      if (!editForm.value.name || !currentCategory.value) return
      
      try {
        currentCategory.value.set('name', editForm.value.name)
        currentCategory.value.set('description', editForm.value.description)
        await currentCategory.value.save()
        await store.dispatch('fetchCategories')
        showEdit.value = false
        showToast('保存成功')
      } catch (error) {
        showToast('保存失败')
      }
    }

    const goToDetail = (item) => {
      router.push(`/item/${item.id}`)
    }

    const getStepsInfo = (item) => {
      const steps = item.get('steps') || []
      const total = steps.length
      const completed = steps.filter(step => step.completed).length
      const percentage = total > 0 ? (completed / total) * 100 : 0
      return { total, completed, percentage }
    }

    const isCompleted = (item) => {
      const steps = item.get('steps') || []
      if (steps.length === 0) return false
      return steps.every(step => step.completed)
    }

    return {
      categories,
      showDialog,
      showItems,
      showEdit,
      currentCategory,
      categoryItems,
      newCategory,
      editForm,
      getRandomColor,
      getCategoryItemCount,
      showCategoryItems,
      createCategory,
      showCreateDialog,
      showEditCategory,
      saveEdit,
      goToDetail,
      getStepsInfo,
      isCompleted
    }
  }
}
</script>

<style scoped>
.categories {
  min-height: 100vh;
  background-color: #f5f5f7;
  display: flex;
  flex-direction: column;
}

.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(245, 245, 247, 0.9);
  backdrop-filter: blur(20px);
  padding: 16px 20px;
}

.title {
  font-size: 34px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.category-list {
  display: grid;
  gap: 12px;
}

.category-item {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.2s;
  cursor: pointer;
}

.category-item:active {
  transform: scale(0.98);
}

.category-info {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.category-icon {
  width: 48px;
  height: 48px;
  background: #f5f5f7;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-details {
  flex: 1;
}

.category-name {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 4px;
}

.category-desc {
  font-size: 14px;
  color: #86868b;
  margin: 0 0 8px;
  line-height: 1.4;
}

.category-stats {
  font-size: 13px;
  color: #86868b;
}

.arrow-icon {
  color: #86868b;
  font-size: 16px;
}

.add-button {
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom) + 70px);
  right: 20px;
  z-index: 999;
  width: auto;
  padding: 0 24px;
  height: 44px;
  font-size: 16px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.items-popup {
  display: flex;
  flex-direction: column;
  background: #f5f5f7;
}

.popup-header {
  background: #fff;
  border-bottom: 1px solid #f5f5f7;
}

:deep(.van-nav-bar) {
  background: transparent;
}

:deep(.van-nav-bar__title) {
  font-weight: 600;
}

.popup-content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
}

.items-list {
  display: grid;
  gap: 12px;
}

.item-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
}

.item-status {
  margin-bottom: 12px;
}

.item-title {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px;
}

.item-desc {
  font-size: 14px;
  color: #86868b;
  margin: 0 0 12px;
  line-height: 1.4;
}

.item-progress {
  margin-top: 12px;
}

.progress-text {
  display: flex;
  justify-content: space-between;
  color: #86868b;
  font-size: 13px;
  margin-bottom: 4px;
}

.progress-bar {
  height: 3px;
  background: #f5f5f7;
  border-radius: 1.5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #0071e3;
  border-radius: 1.5px;
  transition: width 0.3s ease;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 200px);
}

.empty-hint {
  color: #86868b;
  font-size: 14px;
  margin: 8px 0 0;
}

.empty-items {
  margin: 60px 0;
}

:deep(.van-tag--primary) {
  background: rgba(0, 122, 255, 0.1);
  color: #007AFF;
  border: none;
}

:deep(.van-tag--success) {
  background: rgba(52, 199, 89, 0.1);
  color: #34C759;
  border: none;
}

.create-dialog, .edit-dialog {
  :deep(.van-dialog__header) {
    padding: 20px 0;
    font-weight: 600;
  }
  
  :deep(.van-field) {
    padding: 12px 16px;
  }
}

/* 适配不同设备 */
@supports not (padding: env(safe-area-inset-bottom)) {
  .add-button {
    bottom: 80px;
  }
}

@media screen and (max-height: 667px) {
  .add-button {
    bottom: 65px;
  }
}

:deep(.van-button--primary) {
  background: #007AFF;
  border: none;
}

:deep(.van-button--primary:active) {
  opacity: 0.9;
}
</style> 